<template>
  <b-sidebar
    id="sidebar"
    :visible="isSidebarActive"
    bg-variant="white"
    sidebar-class="sidebar-lg"
    shadow
    backdrop
    no-header
    no-close-on-backdrop
    right
    @shown="loadData"
    @hidden="resetForm"
    @change="(val) => $emit('update:is-sidebar-active', val)"
  >
    <template #default="{ hide }">
      <div
        class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1"
      >
        <h5 class="mb-0 text-capitalize">
          {{
            `${isAdd ? $t("actions.add") : $t("actions.edit")} ${$t(
              "maintenance.maintenance_crew"
            )}`
          }}
        </h5>
        <feather-icon
          class="ml-1 cursor-pointer"
          icon="XIcon"
          size="16"
          @click="hide"
        />
      </div>

      <validation-observer #default="{ handleSubmit }" ref="refFormObserver">
        <b-form
          class="p-2"
          @submit.prevent="handleSubmit(onSubmit)"
          @reset.prevent="resetForm"
        >
          <!-- Description -->
          <validation-provider
            #default="validationContext"
            :name="$t('maintenance.crew.description')"
            rules="required"
          >
            <b-form-group
              :label="$t('maintenance.crew.description')"
              label-for="description"
              :state="getValidationState(validationContext)"
            >
              <b-form-input
                id="description"
                v-model="itemData.description"
                :state="getValidationState(validationContext)"
                trim
              />
              <b-form-invalid-feedback>
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <!-- Maintenance Company -->
          <validation-provider
            #default="validationContext"
            :name="$t('maintenance.crew.company')"
            rules="required"
          >
            <b-form-group
              :label="$t('maintenance.crew.company')"
              label-for="id_maintenance_company"
              :state="getValidationState(validationContext)"
            >
              <v-select
                v-model="itemData.id_maintenance_company"
                :options="maintenanceCompanyOptions"
                :reduce="(val) => val.value"
                input-id="id_maintenance_company"
              />
              <b-form-invalid-feedback>
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <!-- Leader -->
          <validation-provider
            #default="validationContext"
            :name="$t('maintenance.crew.leader')"
            rules="required"
          >
            <b-form-group
              :label="$t('maintenance.crew.leader')"
              label-for="id_leader"
              :state="getValidationState(validationContext)"
            >
              <UsersSelectPaginated
                v-model="itemData.id_leader"
                :multiple="false"
                :placeholder="$t('users.search_leader')"
              />
              <b-form-invalid-feedback>
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <!-- Users -->
          <validation-provider
            #default="validationContext"
            :v-bind="$t('maintenance.crew.users')"
            rules="required"
          >
            <b-form-group
              :label="$t('maintenance.crew.users')"
              label-for="users"
              :state="getValidationState(validationContext)"
            >
              <UsersSelectPaginated
                v-model="itemData.users"
                :multiple="true"
                :placeholder="$t('users.search_users')"
              />
              <b-form-invalid-feedback>
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <!-- Submit Buttons -->
          <div class="d-flex mt-2">
            <b-button variant="primary" class="mr-2" type="submit">
              {{ isAdd ? $t("actions.add") : $t("actions.edit") }}
            </b-button>
            <b-button variant="outline-secondary" @click="hide">
              {{ $t("actions.cancel") }}
            </b-button>
          </div>
        </b-form>
      </validation-observer>
    </template>
  </b-sidebar>
</template>

<script>
import {
  BSidebar,
  BForm,
  BFormGroup,
  BFormInput,
  BFormInvalidFeedback,
  BButton,
} from "bootstrap-vue";
import { ValidationProvider, ValidationObserver } from "vee-validate";
import vSelect from "vue-select";
import { ref } from "@vue/composition-api";
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import store from "@/store";
import formValidation from "@core/comp-functions/forms/form-validation";
import UsersSelectPaginated from "@/components/UsersSelectPaginated.vue"; // ajusta la ruta

export default {
  components: {
    BSidebar,
    BForm,
    BFormGroup,
    BFormInput,
    BFormInvalidFeedback,
    BButton,
    vSelect,
    ValidationProvider,
    ValidationObserver,
    UsersSelectPaginated,
  },
  model: {
    prop: "isSidebarActive",
    event: "update:is-sidebar-active",
  },
  props: {
    isSidebarActive: {
      type: Boolean,
      required: true,
    },
    itemEdit: {
      type: Object,
      required: false,
    },
    maintenanceCompanyOptions: {
      type: Array,
      required: true,
    },
    isAdd: {
      type: Boolean,
      required: true,
    },
  },
  setup(props, { emit }) {
    const itemData = ref({
      description: "",
      id_leader: null, // objeto de UsersSelectPaginated
      id_maintenance_company: null,
      users: [], // array de objetos de UsersSelectPaginated
    });

    const toast = useToast();

    const resetData = () => {
      itemData.value = {
        description: "",
        id_leader: null,
        id_maintenance_company: null,
        users: [],
      };
    };

    const {
      refFormObserver,
      getValidationState,
      resetForm: resetObserver,
    } = formValidation(resetData);

    const loadData = async () => {
      if (!props.isAdd && props.itemEdit) {
        const leaderFromEdit = (() => {
          if (props.itemEdit.leader) {
            const u = props.itemEdit.leader;
            return {
              ...u,
              value: u.id,
              label: u.name || u.email || `User #${u.id}`,
            };
          }
          if (props.itemEdit.id_leader) {
            const id = props.itemEdit.id_leader;
            return {
              id,
              value: id,
              label: `User #${id}`,
            };
          }
          return null;
        })();

        const usersFromEdit = (() => {
          if (Array.isArray(props.itemEdit.users)) {
            return props.itemEdit.users.map((u) =>
              u && u.id
                ? {
                    ...u,
                    value: u.id,
                    label: u.name || u.email || `User #${u.id}`,
                  }
                : u
            );
          }
          return [];
        })();

        itemData.value = {
          description: props.itemEdit.description || "",
          id_leader: leaderFromEdit,
          id_maintenance_company:
            props.itemEdit.id_maintenance_company || null,
          users: usersFromEdit,
          id: props.itemEdit.id,
        };
      } else {
        resetData();
      }
      // Ya no cargamos usuarios desde el store; el select paginado se encarga
    };

    const onSubmit = () => {
      const leader = itemData.value.id_leader;
      const users = itemData.value.users || [];

      const payload = {
        description: itemData.value.description,
        id_maintenance_company: itemData.value.id_maintenance_company,
        id_leader: leader
          ? leader.value || leader.id || leader
          : null,
        users: Array.isArray(users)
          ? users.map((u) => (u ? u.value || u.id || u : null)).filter(Boolean)
          : [],
      };

      const action = props.isAdd
        ? "maintenance_crew/add"
        : "maintenance_crew/edit";
      const dispatchParams = props.isAdd
        ? payload
        : { id: itemData.value.id, params: payload };

      store
        .dispatch(action, dispatchParams)
        .then((response) => {
          if (response && response.success) {
            toast({
              component: ToastificationContent,
              props: {
                title: response.message || "Operación exitosa",
                icon: "CheckIcon",
                variant: "success",
              },
            });
            emit("refetch-data");
            emit("update:is-sidebar-active", false);
          } else {
            toast({
              component: ToastificationContent,
              props: {
                title: response?.message || "Error",
                icon: "AlertTriangleIcon",
                variant: "danger",
              },
            });
          }
        })
        .catch((error) => {
          toast({
            component: ToastificationContent,
            props: {
              title: error?.response?.data?.message || "Error",
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
        });
    };

    const resetForm = () => {
      resetObserver();
    };

    return {
      itemData,
      loadData,
      onSubmit,
      resetForm,
      refFormObserver,
      getValidationState,
    };
  },
};
</script>
